import { Button, Form, Input, Modal, Segmented, Select, Table, message } from 'antd';
import { useEffect, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { formatDateTime } from '../../lib/format';
import { usersApi } from '../../services/resources';
import type { ApprovalStatus, RegistrationItem } from '../../types/api';

const STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: '待审核',
  approved: '已通过',
  rejected: '已驳回',
};

const ROLE_LABEL: Record<string, string> = { student: '学生', teacher: '教师' };

export function AdminRegistrationsPage() {
  const [status, setStatus] = useState<ApprovalStatus>('pending');
  const [role, setRole] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<RegistrationItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState<RegistrationItem | null>(null);
  const [reason, setReason] = useState('');

  async function load() {
    setLoading(true);
    try {
      const data = await usersApi.registrations({ status, role, page, limit: 20 });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      message.error('审核列表加载失败');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, role, page]);

  return (
    <div className="stack">
      <h1 className="page-title">注册审核</h1>
      <p className="meta">
        自助注册账号需审核通过后才能登录。账号为 G＋身份证号码，属敏感信息，仅本页可见。
      </p>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <Segmented
          value={status}
          onChange={(v) => {
            setStatus(v as ApprovalStatus);
            setPage(1);
          }}
          options={[
            { label: '待审核', value: 'pending' },
            { label: '已通过', value: 'approved' },
            { label: '已驳回', value: 'rejected' },
          ]}
        />
        <Select
          allowClear
          placeholder="全部身份"
          style={{ width: 140 }}
          value={role}
          onChange={(v) => {
            setRole(v);
            setPage(1);
          }}
          options={[
            { value: 'student', label: '学生' },
            { value: 'teacher', label: '教师' },
          ]}
        />
        <span className="meta">共 {total} 条</span>
      </div>
      {items.length === 0 && !loading ? (
        <EmptyState text="暂无注册申请。" />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          scroll={{ x: 1040 }}
          pagination={{ current: page, pageSize: 20, total, onChange: setPage }}
          columns={[
            { title: '姓名', dataIndex: 'name' },
            { title: '账号', dataIndex: 'principal', render: (v: string) => <span className="mono">{v}</span> },
            { title: '身份', dataIndex: 'role', render: (v: string) => ROLE_LABEL[v] ?? v },
            {
              title: '班级 / 教研组',
              dataIndex: 'class_name',
              render: (v: string | null) => v || <span className="meta">未填</span>,
            },
            {
              title: '手机号',
              dataIndex: 'phone',
              render: (v: string | null) => (v ? <span className="mono">{v}</span> : <span className="meta">未录</span>),
            },
            { title: '提交时间', dataIndex: 'submitted_at', render: (v: string) => formatDateTime(v) },
            { title: '状态', dataIndex: 'status', render: (v: ApprovalStatus) => STATUS_LABEL[v] },
            { title: '驳回原因', dataIndex: 'reject_reason', render: (v: string | null) => v || '—' },
            {
              title: '操作',
              render: (_, row) =>
                row.status === 'pending' ? (
                  <div className="row">
                    <Button
                      type="primary"
                      size="small"
                      onClick={async () => {
                        await usersApi.approveRegistration(row.id);
                        message.success('已通过');
                        await load();
                      }}
                    >
                      通过
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => {
                        setRejecting(row);
                        setReason('');
                      }}
                    >
                      驳回
                    </Button>
                  </div>
                ) : null,
            },
          ]}
        />
      )}
      <Modal
        title={`驳回 ${rejecting?.name ?? ''} 的注册申请`}
        open={Boolean(rejecting)}
        onCancel={() => setRejecting(null)}
        onOk={async () => {
          const text = reason.trim();
          if (!text) {
            message.warning('请填写驳回原因');
            return;
          }
          if (!rejecting) return;
          await usersApi.rejectRegistration(rejecting.id, text);
          message.success('已驳回，申报人可查看原因后重新提交');
          setRejecting(null);
          await load();
        }}
      >
        <Form layout="vertical">
          <Form.Item label="驳回原因（申报人可见）" required>
            <Input.TextArea rows={3} maxLength={200} showCount value={reason} onChange={(e) => setReason(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
